import React, { useEffect, useState } from 'react';
import { X, RefreshCcw, AlertCircle } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for issuing a MANUAL refund (no linked invoice / quote / receipt).
 * Used to record refunds for payments that pre-date the app.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onIssued: (refund) => void
 */
export const ManualRefundModal = ({ open, onClose, onIssued }) => {
  const password = sessionStorage.getItem('admin_password');
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    amount: '',
    currency: 'CI$',
    reason: '',
    refund_date: new Date().toISOString().slice(0, 10),
    note: '',
    email_after: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setSubmitting(false);
      setForm({
        client_name: '',
        client_email: '',
        amount: '',
        currency: 'CI$',
        reason: '',
        refund_date: new Date().toISOString().slice(0, 10),
        note: '',
        email_after: false,
      });
    }
  }, [open]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Please enter a valid refund amount.'); return; }
    if (!form.client_name.trim() || !form.client_email.trim()) {
      setError('Client name and email are required.'); return;
    }
    if (!form.reason.trim()) { setError('Please provide a reason for the refund.'); return; }

    setSubmitting(true);
    try {
      const reason = form.note.trim()
        ? `${form.reason.trim()}\n\n${form.note.trim()}`
        : form.reason.trim();
      const res = await fetch(`${API_URL}/api/admin/refunds`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_type: 'manual',
          amount: amt,
          currency: form.currency,
          client_name: form.client_name.trim(),
          client_email: form.client_email.trim(),
          reason,
          refund_date: new Date(form.refund_date).toISOString(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (HTTP ${res.status})`);
      }
      const refund = await res.json();

      if (form.email_after) {
        try {
          await fetch(`${API_URL}/api/admin/refunds/${refund.id}/send-email`, {
            method: 'POST',
            headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
          });
        } catch (em) { logger.error('Manual refund email failed:', em); }
      }

      onIssued && onIssued(refund);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to issue refund.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose} data-testid="manual-refund-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
              <RefreshCcw className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Manual Refund</h2>
              <p className="text-xs text-foreground/60">Not linked to any invoice / quote / receipt</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="manual-refund-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Use this for refunds against payments that pre-date the app. The amount is not validated against existing records.</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Client Name *</label>
              <input
                type="text" value={form.client_name}
                onChange={(e) => update('client_name', e.target.value)}
                data-testid="manual-refund-client-name"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Client Email *</label>
              <input
                type="email" value={form.client_email}
                onChange={(e) => update('client_email', e.target.value)}
                data-testid="manual-refund-client-email"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1">Refund Amount *</label>
              <input
                type="number" step="0.01" min="0.01"
                value={form.amount} onChange={(e) => update('amount', e.target.value)}
                placeholder="0.00"
                data-testid="manual-refund-amount"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Currency</label>
              <select
                value={form.currency} onChange={(e) => update('currency', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
              >
                <option value="CI$">CI$</option>
                <option value="US$">US$</option>
                <option value="£">£</option>
                <option value="€">€</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Refund Date</label>
            <input
              type="date" value={form.refund_date}
              onChange={(e) => update('refund_date', e.target.value)}
              data-testid="manual-refund-date"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Reason *</label>
            <textarea
              value={form.reason} onChange={(e) => update('reason', e.target.value)}
              rows={2}
              placeholder="e.g. Pre-app booking cancelled, refund of legacy deposit…"
              data-testid="manual-refund-reason"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Optional note (e.g. original payment date or reference)</label>
            <input
              type="text" value={form.note} onChange={(e) => update('note', e.target.value)}
              placeholder="e.g. Original payment: 12 Jan 2025 via bank transfer"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.email_after}
                   onChange={(e) => update('email_after', e.target.checked)}
                   data-testid="manual-refund-email-after" />
            <span>Email the refund receipt PDF to the client</span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="manual-refund-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    data-testid="manual-refund-submit-btn"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <RefreshCcw className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
              {submitting ? 'Recording…' : 'Record Refund'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualRefundModal;
